import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { useCheckout } from "../hooks/useCheckout";

const PACKAGES = [
  {
    id: "astral_reading",
    name: "Arcana Astral Reading & Seal",
    usd: 49,
    inr: 3999,
    period: "Single Service",
    features: [
      "Comprehensive astrological & sigil analysis",
      "Custom Solomonic sigil PDF & instruction",
      "7-day planetary timing report",
    ],
    popular: false,
  },
  {
    id: "custom_ritual",
    name: "Custom Grimoire Ritual",
    usd: 149,
    inr: 11999,
    period: "Per Ritual Operation",
    features: [
      "Individual ritual ceremony — health, wealth, love, career, dispute or marriage",
      "Consecrated planetary talisman shipped",
      "Direct progress updates",
      "Razorpay encrypted checkout",
    ],
    popular: true,
  },
  {
    id: "master_consultation",
    name: "Grand Wizard Consultation",
    usd: 249,
    inr: 19999,
    period: "60-Minute Session",
    features: [
      "Live video consultation with the master practitioner",
      "Bespoke multi-domain spellcraft strategy",
      "Personalized alchemy & charm guidelines",
      "Priority support for 30 days",
    ],
    popular: false,
  },
];

export const Pricing = () => {
  const { currency, setCurrency, format, formatAlt } = useCurrency();
  const { checkout, loading } = useCheckout();

  return (
    <section id="pricing" data-testid="pricing-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14 md:flex items-end justify-between"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-gold/80 font-mono mb-4">
              Offerings & Tribute
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-parchment max-w-xl">
              Choose your working — secured by Razorpay
            </h2>
          </div>
          <div
            data-testid="pricing-currency-switch"
            className="mt-8 md:mt-0 inline-flex rounded-full border border-gold/30 p-1"
          >
            {["usd", "inr"].map((c) => (
              <button
                key={c}
                data-testid={`currency-option-${c}`}
                onClick={() => setCurrency(c)}
                className={`px-5 py-2 rounded-full text-xs font-mono tracking-widest uppercase transition-colors duration-300 ${
                  currency === c ? "bg-gold text-ink" : "text-parchment-muted hover:text-gold"
                }`}
              >
                {c === "usd" ? "$ USD" : "₹ INR"}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`package-card-${p.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className={`relative rounded-2xl p-9 flex flex-col bg-arcanacard transition-transform duration-500 hover:scale-[1.02] ${
                p.popular
                  ? "border-2 border-gold shadow-[0_0_50px_rgba(212,175,55,0.15)]"
                  : "border border-gold/20 card-glow"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold text-ink text-[10px] uppercase tracking-[0.25em] font-semibold rounded-full px-4 py-1.5">
                  Most Coveted
                </span>
              )}
              <h3 className="font-serif text-xl sm:text-2xl text-parchment mb-1">{p.name}</h3>
              <p className="text-[10px] uppercase tracking-[0.3em] font-mono text-parchment-muted mb-7">
                {p.period}
              </p>
              <div className="mb-8">
                <span
                  data-testid={`package-price-${p.id}`}
                  className="font-display text-4xl sm:text-5xl text-gold text-glow-gold"
                >
                  {format(p.usd, p.inr)}
                </span>
                <span className="block mt-2 text-xs font-mono text-parchment-muted">
                  ≈ {formatAlt(p.usd, p.inr)}
                </span>
              </div>
              <ul className="space-y-3.5 mb-10 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-parchment-muted">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`razorpay-checkout-btn-${p.id}`}
                onClick={() => checkout(p.id, p.name)}
                disabled={loading !== null}
                className={`w-full rounded-full py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2 ${
                  p.popular
                    ? "bg-gold text-ink hover:bg-amber"
                    : "border border-gold/60 text-gold hover:bg-gold/10"
                }`}
              >
                {loading === p.id ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Opening Portal…
                  </>
                ) : (
                  "Begin the Working"
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-parchment-muted font-mono tracking-wider">
          Payments are processed in {currency === "usd" ? "USD" : "INR"} · Razorpay encrypted · UPI, cards & netbanking accepted
        </p>
      </div>
    </section>
  );
};
